import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// ─── Square Import Modal ──────────────────────────────────────────────────────
export function SquareImportModal({ currentUser, onClose, onImported }) {
  const [step, setStep] = useState('preview');
  const [newEmployees, setNewEmployees] = useState([]);
  const [existingEmployees, setExistingEmployees] = useState([]);
  // CJ 2026-06-30 (round 3): the preview now also backfills missing
  // square_id links and reports what it healed. Surfacing the count
  // here so the owner knows opening the modal had a real effect
  // even when newEmployees is 0.
  const [previewLinked, setPreviewLinked] = useState([]);
  // CJ 2026-07-11: preview also refreshes linked users' role/locations
  // from Square (Confirm is disabled when nothing's new). Show what moved.
  const [previewUpdated, setPreviewUpdated] = useState([]);
  const [showExisting, setShowExisting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const headers = currentUser ? { 'x-user-id': String(currentUser.id) } : {};

  useEffect(() => {
    api('POST', '/api/square-import', { confirm: false }, headers)
      .then(d => {
        setNewEmployees(d.new_employees || d.employees || []);
        setExistingEmployees(d.existing_employees || []);
        setPreviewLinked(d.preview_linked_users || []);
        setPreviewUpdated(d.preview_updated_users || []);
        setLoading(false);
      })
      .catch(err => { setError(err.message || 'Could not connect to Square.'); setLoading(false); });
  }, []);

  async function handleConfirm() {
    setLoading(true); setError(null);
    try {
      const r = await api('POST', '/api/square-import', { confirm: true }, headers);
      setResult(r); setStep('done'); setLoading(false);
      onImported && onImported();
    } catch (err) {
      setError(err.message || 'Import failed.'); setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl flex flex-col fade-in shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="bg-ink rounded-t-2xl p-5 flex items-center justify-between">
          <div>
            <p className="text-gold text-xs uppercase tracking-widest mb-0.5">Square Payroll</p>
            <h3 className="text-cream font-bold text-base">Import Employees</h3>
          </div>
          <button onClick={onClose} className="text-cream/50 hover:text-cream text-3xl leading-none ml-4">×</button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-400">
            <div className="text-3xl mb-3">⟳</div>
            <p>Connecting to Square...</p>
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 mb-4">
              {error}
            </div>
            <button onClick={onClose} className="w-full py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50">Close</button>
          </div>
        ) : step === 'done' ? (
          <div className="p-6">
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">✅</div>
              <p className="font-bold text-lg text-ink">{result?.message}</p>
            </div>
            {result?.new_users && result.new_users.length > 0 && (
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-ink uppercase tracking-wide">New accounts ({result.new_users.length})</p>
                  <button
                    onClick={() => {
                      const txt = result.new_users.map(u => `${u.name}\t${u.email}\tPIN: ${u.pin}\tPwd: ${u.temp_password || ''}`).join('\n');
                      navigator.clipboard.writeText(txt);
                    }}
                    className="text-xs px-2 py-1 rounded border border-gray-200 text-gray-600 hover:bg-gray-50">
                    Copy all
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-2 py-1.5 font-semibold text-gray-600">Name</th>
                        <th className="text-left px-2 py-1.5 font-semibold text-gray-600">Email</th>
                        <th className="text-left px-2 py-1.5 font-semibold text-gray-600">PIN</th>
                        <th className="text-left px-2 py-1.5 font-semibold text-gray-600">Password</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.new_users.map((u, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-2 py-1.5 text-ink">{u.name}</td>
                          <td className="px-2 py-1.5 text-gray-600 truncate max-w-[140px]">{u.email}</td>
                          <td className="px-2 py-1.5 font-mono font-semibold text-ink">{u.pin}</td>
                          <td className="px-2 py-1.5 font-mono text-ink">{u.temp_password || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-2">Each new user has a generated temporary password (shown above). Share it over a secure channel — they'll be required to change it on first login.</p>
              </div>
            )}
            <button onClick={onClose} className="w-full py-3 bg-crimson text-white font-semibold rounded-xl hover:bg-crimson-dark">Done</button>
          </div>
        ) : (
          <div className="p-5">
            {previewLinked.length > 0 && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800 mb-3">
                <strong>Auto-linked {previewLinked.length} existing staff to Square</strong> ({previewLinked.map((u) => u.name).join(', ')}). Their shifts will now resolve names instead of showing "(unknown)".
              </div>
            )}
            {previewUpdated.length > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 mb-3">
                <strong>Refreshed {previewUpdated.length} from Square:</strong>{' '}
                {previewUpdated.map((u) => u.role_after ? `${u.name} (${u.role_before} → ${u.role_after})` : u.name).join(', ')}
              </div>
            )}
            {newEmployees.length === 0 ? (
              <p className="text-sm text-gray-500 mb-4">
                <strong>No new staff to import.</strong> Square has {existingEmployees.length} employees and we already have all of them — roles and locations were refreshed from Square just now. If you just added someone in Square, their record may not have synced yet — try again in a minute.
              </p>
            ) : (
              <p className="text-sm text-gray-500 mb-4">
                Square has <strong>{newEmployees.length + existingEmployees.length} employees</strong> total — <strong>{newEmployees.length} new</strong> to import. {existingEmployees.length > 0 ? `${existingEmployees.length} already in Monarch will be skipped.` : ''}
              </p>
            )}
            {newEmployees.length > 0 && (
              <div className="space-y-2 mb-3 max-h-60 overflow-y-auto pr-1">
                {newEmployees.map((emp, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center text-sm font-bold text-gold">
                      {emp.name[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-ink">{emp.name}</p>
                      <p className="text-xs text-gray-400">{emp.email} · {emp.role}</p>
                    </div>
                    <span className="text-xs text-green-600 font-medium">New</span>
                  </div>
                ))}
              </div>
            )}
            {existingEmployees.length > 0 && (
              <div className="mb-4">
                <button onClick={() => setShowExisting(s => !s)}
                  className="text-xs text-gray-500 underline">
                  {showExisting ? 'Hide' : 'Show'} {existingEmployees.length} already-imported staff
                </button>
                {showExisting && (
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto pr-1">
                    {existingEmployees.map((emp, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded border border-gray-200">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-700">{emp.name}</p>
                          <p className="text-[10px] text-gray-400">{emp.email}</p>
                        </div>
                        <span className="text-[10px] text-gray-400">In Monarch</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={handleConfirm} disabled={newEmployees.length === 0}
                className="flex-1 py-3 rounded-xl bg-crimson text-white font-semibold text-sm hover:bg-crimson-dark disabled:opacity-40 disabled:cursor-not-allowed">
                {newEmployees.length === 0 ? 'Nothing to import' : `Import ${newEmployees.length} new`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
